import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import RestaurantCard from "../components/RestaurantCard";
import { fetchRestaurants } from "../lib/api";

const FILTERS = ["All", "Bestseller", "Top Rated", "Pure Veg", "Cheap & Best", "Premium", "New"];

export default function RestaurantsPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    fetchRestaurants().then(setItems).catch(() => setItems([]));
    window.scrollTo({ top: 0 });
  }, []);

  const filtered = useMemo(() => {
    return items.filter((r) => {
      const inFilter = filter === "All" ? true : r.tags?.includes(filter);
      const text = `${r.name} ${r.cuisine} ${r.location}`.toLowerCase();
      const matches = q ? text.includes(q.toLowerCase()) : true;
      return inFilter && matches;
    });
  }, [items, q, filter]);

  return (
    <main data-testid="restaurants-page" className="bg-neutral-50 min-h-screen">
      {/* Header banner */}
      <section className="relative bg-ink-900 text-white pt-32 pb-16 grain overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#84CC16]/20 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[#A3E635] font-bold">Restaurants</div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-3"
          >
            Find your <span className="text-[#84CC16]">next meal.</span>
          </motion.h1>
          <p className="mt-4 text-white/70 max-w-xl">{items.length} curated kitchens across Bangalore — biryani to bistros.</p>
        </div>
      </section>

      {/* Controls */}
      <section className="sticky top-16 md:top-20 z-30 bg-neutral-50/90 backdrop-blur-lg border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-col md:flex-row gap-4 md:items-center">
          <div className="flex items-center gap-2 flex-1 bg-white border border-neutral-200 rounded-full px-4 py-2.5">
            <Search size={16} className="text-neutral-500" />
            <input
              data-testid="restaurants-search-input"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search restaurants, cuisines, areas…"
              className="flex-1 outline-none text-sm bg-transparent text-ink-900 placeholder-neutral-500"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-2 px-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`restaurants-filter-${f.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => setFilter(f)}
                className={`whitespace-nowrap text-sm font-medium px-4 py-2 rounded-full border transition ${
                  filter === f
                    ? "bg-ink-900 text-white border-ink-900"
                    : "bg-white text-ink-900 border-neutral-200 hover:border-[#84CC16]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {filtered.length === 0 ? (
          <div data-testid="restaurants-empty" className="text-center py-24 text-neutral-500">
            No kitchens match. Try a different search.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
              >
                <RestaurantCard r={r} idx={i} />
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
